import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from './ui';
import { Icon, Tabs } from './primitives';
import type { DeploymentStatus } from '../api';

export interface ProjectHeaderTab {
  id: string;
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}

export interface ProjectHeaderProps {
  projectName: string;
  projectId?: string;
  environmentName?: string;
  domain?: string;
  status?: DeploymentStatus;
  tabs?: ProjectHeaderTab[];
  actions?: ReactNode;
}

export function ProjectHeader({
  projectName,
  projectId,
  environmentName,
  domain,
  status,
  tabs,
  actions,
}: ProjectHeaderProps) {
  return (
    <div className="project-header">
      <div className="project-header__inner">
        <div className="project-header__top">
          <div className="project-header__meta">
            <nav className="project-header__breadcrumb" aria-label="Breadcrumb">
              <Link to="/app/projects">Projects</Link>
              <span className="project-header__breadcrumb-sep" aria-hidden="true">/</span>
              {projectId ? (
                <Link to={`/app/projects/${projectId}`}>{projectName}</Link>
              ) : (
                <span>{projectName}</span>
              )}
              {environmentName ? (
                <>
                  <span className="project-header__breadcrumb-sep" aria-hidden="true">/</span>
                  <span aria-current="page">{environmentName}</span>
                </>
              ) : null}
            </nav>

            <div className="project-header__title-row">
              <h1 className="project-header__title">{environmentName ? `${projectName} (${environmentName})` : projectName}</h1>
              {environmentName ? (
                <span className="project-header__env-badge">{environmentName}</span>
              ) : null}
              {domain ? (
                <a
                  href={`https://${domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-header__domain"
                >
                  {domain}
                  <Icon name="external-link" size={12} />
                  <span className="visually-hidden"> (opens in a new tab)</span>
                </a>
              ) : null}
              {status ? <StatusBadge kind="legacyDeployment" value={status} /> : null}
            </div>
          </div>

          {actions ? <div className="project-header__actions">{actions}</div> : null}
        </div>

        {tabs && tabs.length > 0 ? (
          <Tabs
            label="Environment sections"
            variant="primary"
            value={(tabs.find((tab) => tab.active) ?? tabs[0]!).id}
            onChange={(id) => tabs.find((tab) => tab.id === id)?.onClick()}
            options={tabs.map((tab) => ({
              value: tab.id,
              label: tab.label,
              count: typeof tab.count === 'number' && tab.count > 0 ? tab.count : undefined,
            }))}
          />
        ) : null}
      </div>
    </div>
  );
}
